<template>
  <v-container>
    <v-card>
      <v-card-title>Payments</v-card-title>
      <v-card-text style="text-align: center" v-if="!inited">
        <v-progress-circular indeterminate />
      </v-card-text>
      <v-container v-if="inited">
        <v-row>
          <v-col cols="3">
            <v-select
              :items="optionsFromEnums.PaymentStatus"
              label="Payment status"
              v-model="filter_payment_status"
              :disabled="!loaded"
            />
          </v-col>
          <v-col cols="3">
            <v-autocomplete
              :items="optionsFromEnums.Fee"
              label="Fee type"
              v-model="filter_fee_type"
              :disabled="!loaded"
            />
          </v-col>
          <v-col cols="3">
            <v-text-field
              label="Description"
              v-model="filter_payment_description"
              :disabled="!loaded"
              @change="refresh(true)"
            />
          </v-col>
          <v-col cols="4">
            <MenuDatePicker
              v-model="filter_start_date"
              label="Received Date (start)"
              :disabled="!loaded"
            />
          </v-col>
          <v-col cols="4">
            <MenuDatePicker
              v-model="filter_end_date"
              label="Received Date (end)"
              :disabled="!loaded"
            />
          </v-col>
          <v-col cols="6">
            <v-autocomplete
              cache-items
              clearable
              :items="personSearchItems"
              :loading="personSearchLoading"
              :search-input.sync="personSearch"
              label="Person"
              v-model="filter_person_id"
              :disabled="!loaded"
              @change="refresh(true)"
            />
          </v-col>
          <v-col cols="6">
            <v-autocomplete
              cache-items
              clearable
              :items="batchSearchItems"
              :loading="batchSearchLoading"
              :search-input.sync="batchSearch"
              label="Batch"
              v-model="filter_batch_no"
              :disabled="!loaded"
              @change="refresh(true)"
            />
          </v-col>
        </v-row>
        <v-row v-if="loaded">
          <v-col>
            <v-btn @click="refresh(true)" v-if="cardinality > 0"
              ><v-icon>mdi-refresh</v-icon> Reload</v-btn
            >
            &nbsp;
            <v-btn @click="startDownloadWorkflow()" v-if="cardinality > 0"
              ><v-icon>mdi-download</v-icon> Download</v-btn
            >
            &nbsp;
          </v-col>
        </v-row>
      </v-container>
      <v-data-table
        :items="entries"
        :options.sync="tableOptions"
        :headers="headers"
        :server-items-length="cardinality"
        v-if="inited"
        :loading="!loaded"
        loading-text="Fetching payments..."
        :footer-props="{
          'items-per-page-options': [10, 20, 50, 100, 150, 200, -1],
        }"
      >
        <template v-slot:item._batch="{ item }">
          <span
            style="color: #4040ff; text-decoration: underline; cursor: pointer"
            title="Display payments for this batch"
            @click="setupBatchFilter(item._batch_no)"
            >{{ item._batch }}</span
          >
        </template>
        <template v-slot:item._person="{ item }">
          <span
            style="color: #4040ff; text-decoration: underline; cursor: pointer"
            title="Display payments for this person"
            @click="setupPersonFilter(item.personid)"
            >{{ item._person }}</span
          >
        </template>
      </v-data-table>
    </v-card>
    <v-dialog model v-model="in_download_mode" :persistent="downloading">
      <v-card>
        <v-card-title> Download search results </v-card-title>
        <v-card-text v-if="download_cardinality == null">
          <v-progress-circular indeterminate />
          Loading result details...
        </v-card-text>
        <v-card-text
          v-if="
            download_cardinality != null &&
            !downloading &&
            download_payload == null
          "
        >
          You are about to begin downloading a spreadsheet file containing the
          results of your search, filtered and sorted as specified.<br /><br />
          This download will contain an estimated
          <strong>{{ download_cardinality }}</strong> rows. The download will be
          a snapshot as of <strong>{{ download_snapshot_as_of }}</strong
          >.<br /><br />
          <v-alert v-if="download_cardinality > 20000" type="warning">
            This is a lot of data. Please note that the download may take a
            while to complete, or may not complete successfully.<br /><br />
            Consider filtering the data before proceeding with the download.
          </v-alert>
          <v-btn @click="performDownload()" v-if="!downloading"
            ><v-icon>mdi-download</v-icon> Begin download</v-btn
          >
        </v-card-text>
        <v-card-text v-if="downloading">
          <v-progress-linear :value="download_progress" />
        </v-card-text>
        <v-card-text v-if="download_payload">
          <v-alert type="info">
            The download has completed successfully.
          </v-alert>
          <JsonExcel
            type="csv"
            :data="download_payload"
            :name="download_filename"
          >
            <v-btn><v-icon>mdi-save</v-icon> Save file</v-btn>
          </JsonExcel>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment-timezone";
import JsonExcel from "vue-json-excel";
import MenuDatePicker from "@/views/admin/date-picker.vue";

function format_amount(x, include_dollar) {
  var m = /([0-9]+)([.][0-9]{0,2})?/.exec(x);
  var prefix =
    typeof include_dollar == "undefined" || include_dollar ? "$" : "";
  if (!m) {
    return x;
  } else {
    if (!m[2]) {
      return prefix + m[1] + ".00";
    } else {
      return prefix + m[1] + m[2].padEnd(3, "0");
    }
  }
}

function escape_like(x) {
  return x.replaceAll("[", "[[]").replaceAll("_", "[_]").replace("%", "[%]");
}

export default {
  name: "AdminPaymentsSearchPage",
  components: {
    JsonExcel,
    MenuDatePicker,
  },
  data: function () {
    return {
      loaded: false,
      loading: false,
      inited: false,
      error: false,
      in_download_mode: false,
      downloading: false,
      download_cardinality: null,
      download_progress: 0,
      download_inc: 0,
      download_max_id: null,
      download_snapshot_as_of: null,
      download_payload: null,
      download_filename: "data.csv",
      enums: {},
      optionsFromEnums: {},
      filter_payment_status: "",
      filter_fee_type: "",
      filter_payment_description: "",
      filter_batch_no: "",
      filter_person_id: "",
      filter_start_date: null,
      filter_end_date: null,
      displayUTC: false, // currently never set to true, but here in case that is required later
      tableOptions: {
        sortBy: ["batchitemid"],
        sortDesc: [true],
      },
      cardinality: 0,
      entries: [],
      headers: [
        { text: "ID", value: "batchitemid" },
        { text: "Batch", value: "_batch", sortable: false },
        { text: "Received", value: "_received", sortable: true },
        { text: "Person", value: "_person", sortable: false },
        { text: "Fee Type", value: "_type", sortable: false },
        { text: "Description", value: "paymentref", sortable: false },
        { text: "Amount", value: "_amount", sortable: false },
        { text: "Status", value: "_status", sortable: false },
      ],
      personSearchItems: [],
      personSearch: null,
      personSearchLoading: false,
      batchSearchItems: [],
      batchSearch: null,
      batchSearchLoading: false,
    };
  },
  watch: {
    tableOptions: {
      handler: function (a, b) {
        if (a.sortBy.length != b.sortBy.length) {
          this.refresh(true);
          return;
        }
        if (a.sortDesc.length != b.sortDesc.length) {
          this.refresh(true);
          return;
        }
        for (var x = 0; x < a.sortBy.length; x += 1) {
          if (a.sortBy[x] != b.sortBy[x]) {
            this.refresh(true);
            return;
          }
          if (a.sortDesc[x] != b.sortDesc[x]) {
            this.refresh(true);
            return;
          }
        }
        if (a.itemsPerPage != b.itemsPerPage) {
          this.refresh(true);
          return;
        }
        if (a.page != b.page) {
          this.refresh();
        }
      },
    },
    filter_payment_status: {
      handler: function () {
        this.refresh(true);
      },
    },
    filter_fee_type: {
      handler: function () {
        this.refresh(true);
      },
    },
    filter_person_id: {
      handler: function () {
        this.refresh(true);
      },
    },
    filter_start_date: {
      handler: function () {
        this.refresh(true);
      },
    },
    filter_end_date: {
      handler: function () {
        this.refresh(true);
      },
    },
    displayUTC: {
      handler: function () {
        this.refresh(this.filter_start_date || this.filter_end_date);
      },
    },
    personSearch: {
      handler: async function () {
        if (!this.personSearch) {
          this.personSearchItems = [];
          return;
        }

        var filter = {
          where: {},
          fields: {
            id: true,
            displayName: true,
            firstname: true,
            middlenames: true,
            surname: true,
          },
        };

        if (/^[0-9]{1,11}$/.test(this.personSearch)) {
          filter.where = {
            id: { eq: this.personSearch },
          };
        } else if (this.personSearch.length > 1) {
          filter.where.or = [
            { surname: { like: escape_like(this.personSearch) + "%" } },
            { firstname: { like: escape_like(this.personSearch) + "%" } },
          ];
        } else {
          return;
        }

        this.personSearchLoading = true;
        try {
          var result = await this.$http.get(
            "/people?filter=" + encodeURIComponent(JSON.stringify(filter))
          );
          this.personSearchItems = result.data.map((s) => ({
            value: s.id,
            text: s.displayName,
          }));
        } finally {
          this.personSearchLoading = false;
        }
      },
    },
    batchSearch: {
      handler: async function () {
        if (!this.batchSearch) {
          this.batchSearchItems = [];
          return;
        }

        var filter = {
          where: {
            or: [],
          },
          limit: 75,
          fields: {
            batchid: true,
            batchname: true,
          },
        };

        filter.where.or.push({
          batchname: { like: escape_like(this.batchSearch) + "%" },
        });
        filter.where.or.push({
          batchname: { like: "%[_]" + escape_like(this.batchSearch) + "%" },
        });

        this.batchSearchLoading = true;
        try {
          var result = await this.$http.get(
            "/financialbatches?filter=" +
              encodeURIComponent(JSON.stringify(filter))
          );
          this.batchSearchItems = result.data.map((s) => ({
            value: s.batchid,
            text: s.batchname,
          }));
        } finally {
          this.batchSearchLoading = false;
        }
      },
    },
  },
  async created() {
    try {
      await Promise.all([
        this.loadEnum("PaymentStatus"),
        this.loadEnum("BatchType"),
        this.loadEnum("BatchStatus"),
        this.loadFees(),
      ]);
    } catch (e) {
      this.error = e.message;
    } finally {
      this.inited = true;
    }
  },
  methods: {
    buildFilter() {
      var filter = {};
      filter.limit = this.tableOptions.itemsPerPage;
      filter.skip =
        (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage;
      filter.fields = {
        batchitemid: true,
        batchid: true,
        paymentamount: true,
        personid: true,
        paymentstatusid: true,
        feetypeid: true,
        paymentref: true,
        paymentreceiveddate: true,
      };
      filter.include = [
        {
          relation: "fee",
          scope: {
            fields: {
              feetypeid: true,
              feetype: true,
            },
          },
        },
        {
          relation: "person",
          scope: {
            fields: {
              displayName: true,
              firstname: true,
              middlenames: true,
              surname: true,
            },
          },
        },
        {
          relation: "batch",
          scope: {
            fields: {
              batchtypeid: true,
              batchname: true,
              batchstatusid: true,
              paymentdate: true,
            },
          },
        },
      ];
      if (this.tableOptions.sortBy.length != 0) {
        filter.order = [];
        var sortBy = this.tableOptions.sortBy;
        var sortDesc = this.tableOptions.sortDesc;
        for (var x = 0; x < sortBy.length; x += 1) {
          if (sortBy[x] == "_received") {
            var col = "paymentreceiveddate";
          } else {
            var col = sortBy[x];
          }
          filter.order.push(col + " " + (sortDesc[x] ? "DESC" : "ASC"));
        }
      }
      var conditions = [];
      if (this.filter_payment_status) {
        conditions.push({
          paymentstatusid: { eq: this.filter_payment_status },
        });
      }
      if (this.filter_fee_type) {
        conditions.push({
          feetypeid: { eq: this.filter_fee_type },
        });
      }
      if (this.filter_payment_description) {
        conditions.push({
          paymentref: {
            like: escape_like(this.filter_payment_description) + "%",
          },
        });
      }
      if (this.filter_batch_no) {
        conditions.push({
          batchid: { eq: this.filter_batch_no },
        });
      }
      if (this.filter_person_id) {
        conditions.push({
          personid: { eq: this.filter_person_id },
        });
      }
      if (this.filter_start_date) {
        conditions.push({
          paymentreceiveddate: {
            gte: this.filterDateToConstraint(this.filter_start_date),
          },
        });
      }
      if (this.filter_end_date) {
        conditions.push({
          paymentreceiveddate: {
            lte: this.filterDateToConstraint(
              this.filter_end_date.clone().add(1, "days")
            ),
          },
        });
      }
      if (conditions.length > 0) {
        filter.where = { and: conditions };
      }
      return filter;
    },
    filterDateToConstraint(d) {
      var x;
      if (this.displayUTC) {
        x = moment.utc([d.year(), d.month(), d.date()]);
      } else {
        x = moment
          .tz([d.year(), d.month(), d.date()], "Pacific/Auckland")
          .tz("UTC");
        console.log(x.format());
      }
      return x.format();
    },
    clearFilters() {
      this.filter_payment_status = "";
      this.filter_fee_type = "";
      this.filter_payment_description = "";
      this.filter_start_date = null;
      this.filter_end_date = null;
      this.filter_person_id = "";
      this.filter_batch_no = "";
    },
    async setupBatchFilter(i) {
      this.loaded = false;
      var result = await this.$http.get(
        "/financialbatches/" + encodeURIComponent(i)
      );
      this.batchSearchItems = [{ text: result.data.batchname, value: i }];
      this.clearFilters();
      this.filter_batch_no = i;
      this.refresh(true);
    },
    async setupPersonFilter(i) {
      this.loaded = false;
      var result = await this.$http.get(
        "/people/" +
          encodeURIComponent(i) +
          "?filter=" +
          encodeURIComponent(
            JSON.stringify({
              fields: {
                id: true,
                firstname: true,
                middlenames: true,
                surname: true,
                displayName: true,
              },
            })
          )
      );
      this.personSearchItems = [{ text: result.data.displayName, value: i }];
      this.clearFilters();
      this.filter_person_id = i;
      this.refresh(true);
    },
    async refresh(reset) {
      if (reset) {
        this.cardinality = 0;
        this.tableOptions.page = 1;
      }
      if (this.loading) {
        return;
      }
      this.loading = true;
      this.loaded = false;
      this.entries = [];
      var filter = this.buildFilter();
      if (this.cardinality == 0) {
        var count = (
          await this.$http.get(
            "/financialbatchitems/count" +
              (filter.where
                ? "?where=" + encodeURIComponent(JSON.stringify(filter.where))
                : "")
          )
        ).data.count;
      } else {
        var count = this.cardinality;
      }
      var results = await this.$http.get(
        "/financialbatchitems?filter=" +
          encodeURIComponent(JSON.stringify(filter))
      );
      var entries = [];
      for (var x = 0; x < results.data.length; x += 1) {
        var entry = results.data[x];
        entry["_status"] =
          this.enums.PaymentStatus.get(entry["paymentstatusid"]) || "Unknown";
        entry["_amount"] = format_amount(entry["paymentamount"]);
        entry["_type"] = entry.fee.feetype;
        entry["_person"] = entry.person.displayName;
        entry["_batch"] = entry.batch.batchname;
        entry["_batch_no"] = entry.batchid;
        var ts = moment.parseZone(entry.paymentreceiveddate);
        if (!this.displayUTC) {
          ts = ts.tz("Pacific/Auckland");
        }
        entry["_received"] = ts.format("D MMM YYYY h:mm a z");
        entries.push(entry);
      }
      this.entries = entries;
      this.cardinality = count;
      this.loaded = true;
      this.loading = false;
    },
    async loadEnum(k) {
      var data = (
        await this.$http.get(
          "/lookups?filter=" +
            encodeURIComponent(JSON.stringify({ where: { name: { eq: k } } }))
        )
      ).data;
      data = (
        await this.$http.get(
          "/lookups/" + encodeURIComponent(data[0].id) + "/values"
        )
      ).data;
      this.$set(this.enums, k, new Map(data.map((s) => [s.valueid, s.value])));
      this.$set(
        this.optionsFromEnums,
        k,
        [{ value: "", text: "All" }].concat(
          data.map((s) => ({ value: s.valueid, text: s.value }))
        )
      );
      return this.enums[k];
    },
    async loadFees() {
      var results = await this.$http.get("/fees");
      this.enums.Fees = new Map(results.data.map((s) => [s.feeid, s.feetype]));
      this.$set(
        this.optionsFromEnums,
        "Fee",
        [{ value: "", text: "All" }].concat(
          results.data.map((s) => ({ value: s.feeid, text: s.feetype }))
        )
      );
    },
    async startDownloadWorkflow() {
      this.in_download_mode = true;
      this.download_cardinality = null;
      this.downloading = false;
      this.download_payload = null;
      var inc = (this.download_inc += 1);
      var filter = this.buildFilter();
      try {
        var count_result = await this.$http.get(
          "/financialbatchitems/count" +
            (filter.where
              ? "?where=" + encodeURIComponent(JSON.stringify(filter.where))
              : "")
        );
        filter.limit = 1;
        filter.order = ["batchitemid DESC"];
        var max_result = await this.$http.get(
          "/financialbatchitems?filter=" +
            encodeURIComponent(JSON.stringify(filter))
        );
        if (inc == this.download_inc) {
          this.download_cardinality = count_result.data.count;
          this.download_snapshot_as_of = moment
            .parseZone(max_result.data[0].paymentreceiveddate)
            .tz("Pacific/Auckland")
            .format("D MMM YYYY h:mm a z");
          this.download_max_id = max_result.data[0].batchitemid;
        }
      } catch {
        this.in_download_mode = false;
      }
    },
    async performDownload() {
      this.downloading = true;
      this.download_progress = 0;
      var filter = this.buildFilter();
      delete filter.limit;
      delete filter.skip;
      var buffer = [];
      var result;
      filter.limit = 500;
      filter.skip = 0;
      if (typeof filter.where == "undefined") {
        filter.where = { and: [] };
        this.download_filename =
          "payments-export-" + String(this.download_max_id) + ".csv";
      } else {
        this.download_filename =
          "payments-export-" + String(this.download_max_id) + "-filtered.csv";
      }
      filter.where.and.push({ batchitemid: { lte: this.download_max_id } });
      do {
        result = await this.$http.get(
          "/financialbatchitems?filter=" +
            encodeURIComponent(JSON.stringify(filter))
        );
        for (var item of result.data) {
          var batch_status =
            this.enums.BatchStatus.get(item.batch.batchstatusid) ||
            String(item.batch.batchstatusid);
          var item_trans = {
            BatchRegister: "",
            BatchId: String(item.batchid),
            BatchItemID: String(item.batchitemid),
            WhoFor: "Batch ID: " + String(item.batchid),
            BatchDescription:
              item.batch.batchname +
              "; Status: " +
              batch_status +
              "; Paid: " +
              moment.parseZone(item.batch.paymentdate).format("MM/DD/YYYY"),
            BatchStatus: batch_status,
            PaymentDate: moment
              .parseZone(item.batch.paymentdate)
              .format("M/DD/YYYY h:mm A"),
            PersonName:
              item.person.firstname +
              " " +
              item.person.middlenames +
              " " +
              item.person.surname,
            RegistrationNo: String(item.personid),
            FeeType: item.fee.feetype,
            PaymentAmount: format_amount(item.paymentamount, false),
            PaymentStatus:
              this.enums.PaymentStatus.get(item.paymentstatusid) ||
              String(item.paymentstatusid),
            PaymentReceivedDate: moment
              .parseZone(item.paymentreceiveddate)
              .format("M/DD/YYYY h:mm A"),
            PaymentRef: item.paymentref,
            ROWNUM: "1",

            ReceivedSortable: item.paymentreceiveddate,
            ReceivedSortableNZT: moment
              .parseZone(item.paymentreceiveddate)
              .tz("Pacific/Auckland")
              .format(),
            BatchType:
              this.enums.BatchType.get(item.batch.batchtypeid) ||
              String(item.batch.batchtypeid),
            PersonSurname: item.person.surname,
            PersonFirstname: item.person.firstname,
            PersonMiddlenames: item.person.middlenames,
          };
          buffer.push(item_trans);
        }
        filter.skip += 500;
        this.download_progress =
          (buffer.length * 100) / this.download_cardinality;
      } while (result.data.length > 0);
      this.download_payload = buffer;
      this.downloading = false;
    },
  },
};
</script>
